import React, { useCallback, useEffect, useRef } from 'react'
import { AiOutlineSearch } from 'react-icons/ai'
import PropTypes from 'prop-types'
import { FormControl, FormLabel, IconButton, InputGroup, InputLeftElement, InputRightElement } from '@chakra-ui/react'
import { observer } from 'mobx-react'
import rootStore from '../../../store/RootStore'
import { CustomInput } from '../../../components/ui/CustomInput'
import { FiX } from 'react-icons/fi'

export const MenuSearch = observer(() => {
  const {
    reportStore: {
      reportSearchText,
      searchResetCount,
      setSearch,
      resetReportSearchText,
    },
  } = rootStore

  const inputRef = useRef(null)
  const lastResetRef = useRef(searchResetCount)

  /**
   * Enter submits the term the reader typed: `setSearch` is trailing-debounced 500 ms, so pressing
   * it inside that window has to flush the pending call rather than wait it out. Enter clearing
   * the search instead — through `resetReportSearchText`, which cancels that pending call — meant
   * a term typed and submitted quickly never reached the store at all, and one typed slowly was
   * wiped the moment the reader confirmed it. Clearing has its own control.
   */
  const handleSubmit = useCallback(
    e => {
      if (e.type === 'keyup' && e.keyCode === 13) {
        setSearch.flush()
      }
    },
    [setSearch]
  )

  const handleInputChange = e => {
    setSearch(e.target.value)
  }

  /**
   * The box is uncontrolled (`defaultValue` plus a ref), so it does not follow the store on its
   * own, and the snapshot picker changes the report view without unmounting this component — a
   * view-change reset would otherwise leave the old term sitting in a box the store reads as empty.
   *
   * Keyed on the reset counter rather than on `reportSearchText`, because a reset that only
   * cancels a pending keystroke leaves the text at `''` and MobX does not notify on an unchanged
   * value. Skipping the mount pass is what lets `defaultValue` stand.
   */
  useEffect(() => {
    if (lastResetRef.current === searchResetCount) return
    lastResetRef.current = searchResetCount
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [searchResetCount])

  return (
    <FormControl onKeyUp={handleSubmit}>
      <FormLabel hidden>Search</FormLabel>
      <InputGroup>
        {reportSearchText.length > 0 ? (
          <InputRightElement>
            <IconButton
              variant="link"
              aria-label="clear-search"
              onClick={resetReportSearchText}
              background={'brand.white'}
              minWidth="20px"
              height="20px"
              borderRadius={20}
              _hover={{
                backgroundColor: 'grey',
                color: 'white',
              }}>
              <FiX />
            </IconButton>
          </InputRightElement>
        ) : null}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <AiOutlineSearch
              size={20}
              color={'white'}
            />
          </InputLeftElement>
          <CustomInput
            title="Search"
            placeholder="Find..."
            color="white"
            background="brand.darkBlue"
            outline="none"
            border="none"
            pl={10}
            onChange={handleInputChange}
            defaultValue={reportSearchText}
            ref={inputRef}
          />
        </InputGroup>
      </InputGroup>
    </FormControl>
  )
})

MenuSearch.propTypes = {
  reports: PropTypes.array,
}
